import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Squares2X2Icon,
  PlusIcon,
  CubeIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import {
  getMultiTokenCollection,
  listTokenTypes,
  defineTokenTypeOps,
  mintMultiTokenOps,
  getMultiTokenBalance,
} from '../api/vexforge';
import { signAndSend } from '../lib/submit';

export default function MultiTokenCollection() {
  const { collection } = useParams();
  const { address, isConnected } = useWalletStore();
  const [collectionData, setCollectionData] = useState(null);
  const [tokenTypes, setTokenTypes] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDefine, setShowDefine] = useState(false);
  const [defining, setDefining] = useState(false);
  const [mintingId, setMintingId] = useState(null);

  const [defineForm, setDefineForm] = useState({
    tokenTypeId: '',
    name: '',
    metadataUri: '',
    maxSupply: '',
    isFungible: true,
  });

  const [mintForm, setMintForm] = useState({
    amount: '',
    to: '',
  });

  const isCreator = isConnected && collectionData?.creator &&
    address?.toLowerCase() === collectionData.creator.toLowerCase();

  useEffect(() => {
    async function fetchData() {
      try {
        const colData = await getMultiTokenCollection(collection);
        setCollectionData(colData);

        const types = await listTokenTypes(collection, 100);
        const typeList = Array.isArray(types) ? types : [];
        setTokenTypes(typeList);

        // Fetch balances for each type
        if (isConnected && address) {
          const balMap = {};
          for (const tt of typeList) {
            try {
              const bal = await getMultiTokenBalance(collection, tt.token_type_id || tt.id, address);
              if (bal && bal !== '0') balMap[tt.token_type_id || tt.id] = bal;
            } catch {
              // Skip
            }
          }
          setBalances(balMap);
        }
      } catch (err) {
        console.error('Failed to fetch collection:', err);
      } finally {
        setLoading(false);
      }
    }
    if (collection) fetchData();
  }, [collection, address, isConnected]);

  const handleDefine = async (e) => {
    e.preventDefault();
    setDefining(true);
    try {
      const ops = defineTokenTypeOps(
        collection,
        defineForm.tokenTypeId,
        defineForm.name,
        defineForm.metadataUri,
        defineForm.maxSupply || null,
        defineForm.isFungible,
      );
      const txHash = await signAndSend(ops, `Define token type #${defineForm.tokenTypeId} "${defineForm.name}"`);
      if (!txHash) return;
      toast.success(`Token type submitted (tx ${String(txHash).slice(0, 14)}…) — refresh once confirmed`);
      setShowDefine(false);
    } catch (err) {
      toast.error(err.message || 'Failed to define token type');
    } finally {
      setDefining(false);
    }
  };

  const handleMint = async (tokenTypeId) => {
    if (!mintForm.amount) return;
    setMintingId(tokenTypeId);
    try {
      const ops = mintMultiTokenOps(collection, tokenTypeId, mintForm.to || address, mintForm.amount);
      const txHash = await signAndSend(ops, `Mint ${mintForm.amount} of token type #${tokenTypeId}`);
      if (txHash) {
        toast.success(`Mint submitted (tx ${String(txHash).slice(0, 14)}…)`);
        setMintForm({ amount: '', to: '' });
      }
    } catch (err) {
      toast.error(err.message || 'Mint failed');
    } finally {
      setMintingId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-gray-400">Loading collection...</div>;
  }

  if (!collectionData) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Collection Not Found</h1>
        <p className="text-gray-400">Could not load collection {collection}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-vextaris flex items-center justify-center">
              <Squares2X2Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{collectionData.name}</h1>
              <p className="text-vextaris-blue font-mono text-sm">{collectionData.symbol}</p>
            </div>
          </div>

          {isCreator && (
            <button
              onClick={() => setShowDefine(!showDefine)}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-vextaris text-white rounded-lg text-sm font-medium hover:opacity-90"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Define Type</span>
            </button>
          )}
        </div>

        {collectionData.description && (
          <p className="text-gray-400 mt-4">{collectionData.description}</p>
        )}

        <div className="flex space-x-6 mt-4 text-sm">
          <div>
            <span className="text-gray-400">Royalty: </span>
            <span className="text-white">{(collectionData.royalty_bps || 0) / 100}%</span>
          </div>
          <div>
            <span className="text-gray-400">Token Types: </span>
            <span className="text-white">{tokenTypes.length}</span>
          </div>
          <div>
            <span className="text-gray-400">Creator: </span>
            <span className="text-white font-mono text-xs">{collectionData.creator ? `${collectionData.creator.slice(0, 10)}...${collectionData.creator.slice(-6)}` : '--'}</span>
          </div>
        </div>
      </div>

      {/* Define Type Form */}
      {showDefine && (
        <form onSubmit={handleDefine} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Define New Token Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Type ID</label>
              <input type="number" value={defineForm.tokenTypeId} onChange={e => setDefineForm(p => ({ ...p, tokenTypeId: e.target.value }))} required min="0"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Name</label>
              <input type="text" value={defineForm.name} onChange={e => setDefineForm(p => ({ ...p, name: e.target.value }))} required
                placeholder="Gold Coin" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Metadata URI</label>
              <input type="text" value={defineForm.metadataUri} onChange={e => setDefineForm(p => ({ ...p, metadataUri: e.target.value }))} required
                placeholder="ipfs://..." className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Max Supply (optional)</label>
              <input type="text" value={defineForm.maxSupply} onChange={e => setDefineForm(p => ({ ...p, maxSupply: e.target.value }))}
                placeholder="Leave blank for unlimited" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple" />
            </div>
          </div>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" checked={defineForm.isFungible} onChange={e => setDefineForm(p => ({ ...p, isFungible: e.target.checked }))}
              className="w-5 h-5 rounded border-vextaris-purple/30 bg-vextaris-darker text-vextaris-purple focus:ring-vextaris-purple" />
            <span className="text-gray-300">Fungible (stackable quantity)</span>
          </label>
          <button type="submit" disabled={defining}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50">
            {defining ? 'Defining...' : 'Define Type'}
          </button>
        </form>
      )}

      {/* Token Types List */}
      {tokenTypes.length === 0 ? (
        <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
          <Squares2X2Icon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No token types defined yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tokenTypes.map((tt, i) => {
            const typeId = tt.token_type_id || tt.id;
            const bal = balances[typeId];
            return (
              <div key={i} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {tt.is_fungible ? (
                      <CubeIcon className="w-8 h-8 text-vextaris-blue" />
                    ) : (
                      <PhotoIcon className="w-8 h-8 text-vextaris-purple" />
                    )}
                    <div>
                      <p className="text-white font-medium">{tt.name || `Type #${typeId}`}</p>
                      <div className="flex space-x-3 text-xs text-gray-400 mt-1">
                        <span className={`px-2 py-0.5 rounded ${tt.is_fungible ? 'bg-vextaris-blue/10 text-vextaris-blue' : 'bg-vextaris-purple/10 text-vextaris-purple'}`}>
                          {tt.is_fungible ? 'Fungible' : 'Non-Fungible'}
                        </span>
                        <span>ID: {typeId}</span>
                        <span>Supply: {tt.current_supply || 0}{tt.max_supply ? ` / ${tt.max_supply}` : ''}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    {bal && (
                      <p className="text-white font-semibold mb-1">Your balance: {bal}</p>
                    )}
                    {isCreator && (
                      <div className="flex items-center space-x-2">
                        {mintingId === typeId ? (
                          <>
                            <input type="text" value={mintForm.amount}
                              onChange={e => setMintForm(p => ({ ...p, amount: e.target.value }))}
                              placeholder="Amount" className="w-24 px-2 py-1 bg-vextaris-darker border border-vextaris-purple/30 rounded text-white text-sm focus:outline-none" />
                            <button onClick={() => handleMint(typeId)}
                              className="px-3 py-1 bg-gradient-vextaris text-white rounded text-xs font-medium hover:opacity-90">
                              Mint
                            </button>
                            <button onClick={() => setMintingId(null)}
                              className="px-2 py-1 text-gray-400 text-xs hover:text-white">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setMintingId(typeId)}
                            className="px-3 py-1 bg-vextaris-purple/10 text-vextaris-blue rounded text-xs font-medium hover:bg-vextaris-purple/20">
                            Mint
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}